import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Loader2, Shield, Mail } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import MicrosoftSsoButton from "@/components/MicrosoftSsoButton";

interface LoginProps {
  onLoginSuccess: () => void;
}

export default function Login({ onLoginSuccess }: LoginProps) {
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expiresAt, setExpiresAt] = useState<string>('');

  const { requestOtp, verifyOtp, isSsoEnabled, isLoadingEntraConfig } = useAuth();

  // Check if we're in development mode
  const isDev = import.meta.env.MODE === 'development' || import.meta.env.DEV;

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Validate email format
      if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        throw new Error('Please enter a valid email address');
      }

      const result = await requestOtp.mutateAsync({
        email
      });

      setExpiresAt(result.expiresAt);
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Validate OTP code format
      if (!otpCode.match(/^[0-9]{4}$/)) {
        throw new Error('Please enter a valid 4-digit OTP code');
      }

      const result = await verifyOtp.mutateAsync({
        email,
        code: otpCode
      });

      // Check if user has CMS access (check both roles and accessTypes)
      const hasRoleAccess = result.user.roles?.some(role => ['AthlynAdmin', 'AthlynTeam'].includes(role));
      const hasAccessTypePermission = result.user.accessTypes?.some(accessType =>
        ['CMS', 'Admin', 'AthlynAdmin', 'AthlynTeam'].includes(accessType)
      );

      if (!hasRoleAccess && !hasAccessTypePermission) {
        throw new Error('Access denied. You do not have permission to access the CMS portal.');
      }

      // Success - authentication hook will store the token
      onLoginSuccess();
    } catch (err: any) {
      setError(err.message || 'Invalid OTP code. Please try again.');
      // Reset OTP input on error
      setOtpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setOtpCode('');
    setError('');
  };

  const formatExpiryTime = () => {
    if (!expiresAt) return '';
    const expiryTime = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiryTime.getTime() - now.getTime();
    const diffMins = Math.ceil(diffMs / 60000);
    return diffMins > 0 ? `${diffMins} minute${diffMins > 1 ? 's' : ''}` : '0 minutes';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Athlyn CMS Portal</CardTitle>
          <CardDescription>
            {step === 'email'
              ? 'Sign in to access the CMS portal'
              : 'Enter the 4-digit code sent to your email'
            }
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 'email' ? (
            <div className="space-y-4">
              {/* Microsoft SSO Option */}
              {!isLoadingEntraConfig && isSsoEnabled && (
                <>
                  <MicrosoftSsoButton
                    onLoginSuccess={onLoginSuccess}
                    disabled={isLoading}
                  />

                  {/* Only show OTP option in development mode */}
                  {isDev && (
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Email OTP Login Form - Only show in development mode */}
              {isDev && (
                <form onSubmit={handleRequestOtp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    Enter your registered email address
                  </p>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP Code'
                  )}
                </Button>
              </form>
              )}
            </div>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="text-center text-sm text-gray-600 mb-4">
                Code sent to <strong>{email}</strong>
                {expiresAt && (
                  <div className="mt-1">
                    Expires in {formatExpiryTime()}
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="otpCode">4-Digit OTP Code</Label>
                <Input
                  id="otpCode"
                  type="text"
                  placeholder="1234"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className="text-center text-2xl font-mono tracking-widest"
                  maxLength={4}
                  required
                  autoFocus
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading || otpCode.length !== 4}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Access CMS'
                )}
              </Button>
            </form>
          )}
        </CardContent>

        {step === 'otp' && (
          <CardFooter className="flex flex-col space-y-2">
            <Button variant="ghost" onClick={handleBackToEmail} className="w-full">
              ← Use Different Email Address
            </Button>
            <Button
              variant="outline"
              onClick={() => handleRequestOtp(new Event('click') as any)}
              disabled={isLoading}
              className="w-full"
            >
              Resend OTP Code
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}